import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Stack,
  Alert,
  CircularProgress,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  createStorage,
  getMoveService,
  saveVolumne,
  updateMoveService,
} from "../../redux/action/move-service";
import StairsIcon from "../../assets/images/StairsIcon.png";
import ElevatorIcon from "../../assets/images/Elevator.png";
import NotAvailableIcon from "../../assets/images/N:A-Icon.png";
import Header from "../header/Header";

const storageSizeOptions = [
  { label: "5x5x8", value: 200 },
  { label: "5x10x8", value: 400 },
  { label: "5x15x8", value: 600 },
  { label: "10x10x8", value: 800 },
  { label: "10x15x8", value: 1200 },
  { label: "10x20x8", value: 1600 },
  { label: "10x25x8", value: 2000 },
  { label: "10x30x8", value: 2400 },
  { label: "20x20x8", value: 3200 },
];

const storageSizeOptionsForSquareFeet = [
  { label: "5x5", value: 25 },
  { label: "5x10", value: 50 },
  { label: "5x15", value: 75 },
  { label: "10x10", value: 100 },
  { label: "10x15", value: 150 },
  { label: "10x20", value: 200 },
  { label: "10x25", value: 250 },
  { label: "10x30", value: 300 },
  { label: "20x20", value: 400 },
];

const Storage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [storageSize, setStorageSize] = useState("");
  const [storageFilled, setStorageFilled] = useState(70);
  const [accessType, setAccessType] = useState<
    "elevator" | "stairs" | "not-applicable" | ""
  >("");
  const [floorNumber, setFloorNumber] = useState("");
  const [floorNumberError, setFloorNumberError] = useState("");
  const [error, setError] = useState("");
  const [moveId, setMoveId] = useState(null);
  const [storageId, setStorageId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { businessName, carrierCanonicalId,canonicalId } = useParams();
  const lastStep = 7;
  const [moveService, setMoveService] = useState<any>(null);
  useEffect(() => {
    if (canonicalId) {
      dispatch<any>(getMoveService(canonicalId))
        .then(unwrapResult)
        .then((res: any) => {
          setMoveService(res);
          setMoveId(res.id);
          setStorageId(res.id.storageId);
        })
        .catch((err: any) => {
          console.error("Failed to fetch move data:", err);
        });
    }
  }, []);

  useEffect(() => {
    const updateLastStep = async () => {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .then((res: any) => {});
    };

    if (canonicalId) {
      updateLastStep();
    }
  }, [canonicalId, dispatch, lastStep]);

  const handleContinue = async () => {
    if (!storageSize) {
      setError("Please select a storage size");
      return;
    }
    if (!accessType) {
      setError("Please select access type");
      return;
    }
    if (accessType !== "not-applicable" && !floorNumber) {
      setError("Please enter floor number");
      return;
    }

    setError("");
    setIsLoading(true);
    setLoadingMessage("Saving storage information...");

    try {
      const storageData = {
        storage_size: storageSize.toString(),
        storage_unit: storageFilled.toString(),
        isElevator: accessType === "elevator",
        isStairs: accessType === "stairs",
        not_applicable: accessType === "not-applicable",
        storage_floor_number: floorNumber,
        moveService_id: moveId,
        storage_cubic_feet:
          moveService.distance > 200
            ? (Number(storageFilled) / 100) * Number(storageSize)
            : null,
      };
      dispatch<any>(createStorage(storageData))
        .then(unwrapResult)
        .then((res: any) => {
          setLoadingMessage("Finalizing your storage details...");
          if (moveService.distance > 200) {
            dispatch<any>(saveVolumne(canonicalId || ""))
              .then(unwrapResult)
              .then((res: any) => {
                navigate(
                  `/${businessName}/${carrierCanonicalId}/delivery-details/${canonicalId}`
                );
              })
              .catch((err: any) => {
                console.log(err);
                setIsLoading(false);
              });
          } else {
            const body = {
              canonical_id: canonicalId,
              pickup_square_footage:
                (Number(storageFilled) / 100) * Number(storageSize),
            };
            dispatch<any>(updateMoveService(body))
              .then(unwrapResult)
              .then((response: any) => {
                navigate(`/${businessName}/${carrierCanonicalId}/delivery-details/${canonicalId}`);
              })
              .catch((err: any) => {
                console.log(err);
              });
          }
        });
    } catch (err: any) {
      setError(err?.message || "Failed to create storage information");
      setIsLoading(false);
    }
  };

  const handleFloorNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    const numberValue = parseInt(value);
    if (numberValue > 20) {
      setFloorNumberError("Floor number cannot be greater than 20");
      setFloorNumber("20");
    } else {
      setFloorNumberError("");
      setFloorNumber(value);
    }
  };

  const AccessOption = ({
    type,
    icon,
    label,
  }: {
    type: "elevator" | "stairs" | "not-applicable";
    icon: string;
    label: string;
  }) => (
    <Button
      fullWidth
      variant={accessType === type ? "contained" : "outlined"}
      onClick={() => {
        setAccessType(type);
        if (type === "not-applicable") setFloorNumber("0");
      }}
      sx={{
        height: 120,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderColor: theme.palette.primary.main,
        "&:hover": {
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <img
        src={icon}
        alt={label}
        style={{
          width: 50,
          height: 50,
          filter: type === "stairs" ? "invert(1)" : "none",
        }}
      />
      <Typography fontSize={14}>{label}</Typography>
    </Button>
  );

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Header />
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Container
        sx={{
          width: "100%",
          height: isMobile ? "93vh" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "20px" : "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            fontWeight={700}
            fontSize={isMobile ? "16px" : "18px"}
            textAlign="center"
            mb={2}
          >
            Storage Information
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />
          <Box mb={3}>
            <Typography fontWeight={600} mb={1}>
              Storage Size
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Select storage size</InputLabel>
              <Select
                value={storageSize}
                onChange={(e) => setStorageSize(e.target.value)}
                label="Select storage size"
              >
                {moveService?.distance > 200
                  ? storageSizeOptions.map((size) => (
                      <MenuItem key={size.value} value={size.value}>
                        {size.label}
                      </MenuItem>
                    ))
                  : storageSizeOptionsForSquareFeet.map((size) => (
                      <MenuItem key={size.value} value={size.value}>
                        {size.label}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </Box>
          <Box mb={3}>
            <Typography fontWeight={600} mb={1}>
              Storage Fill Percentage
            </Typography>
            <Slider
              value={storageFilled}
              onChange={(_, value) => setStorageFilled(value as number)}
              valueLabelDisplay="auto"
              step={10}
              marks={[
                { value: 0, label: "0%" },
                { value: 100, label: "100%" },
              ]}
            />
          </Box>
          <Box mb={3}>
            <Typography fontWeight={600} mb={1}>
              Access Type
            </Typography>
            <Stack direction="row" spacing={2} mb={2}>
              <AccessOption type="stairs" icon={StairsIcon} label="Stairs" />
              <AccessOption
                type="elevator"
                icon={ElevatorIcon}
                label="Elevator"
              />
              <AccessOption
                type="not-applicable"
                icon={NotAvailableIcon}
                label="Not Applicable"
              />
            </Stack>
            {accessType && accessType !== "not-applicable" && (
              <>
                <Typography fontWeight={600} mb={1}>
                  Floor Number
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={floorNumber}
                  onChange={handleFloorNumberChange}
                  inputProps={{ min: 0, max: 20 }}
                />
                {floorNumberError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {floorNumberError}
                  </Alert>
                )}
              </>
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: "20px",
            marginTop: "auto",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              borderRadius: "20px",
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
            }}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: "20px",
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
              "&:hover": { backgroundColor: "#357ABD" },
              "&:disabled": {
                backgroundColor: "#cccccc",
                color: "#666666",
              },
            }}
            onClick={handleContinue}
            disabled={
              !storageSize ||
              !accessType ||
              (accessType !== "not-applicable" && !floorNumber)
            }
          >
            Continue
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default Storage;
